import React, { useState } from 'react';
import { 
  Users, 
  Zap, 
  Mail, 
  Calendar, 
  BarChart3, 
  Bot,
  Check,
  Play,
  Cog,
  Palmtree,
  Sparkles,
  X,
  Building2,
  Target,
  Star,
  Wrench,
  Layers,
  CalendarDays,
  PhoneIncoming
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderPill from './AnimatedBorderPill';

interface FeatureCard {
  title: string;
  description: string;
  icon: React.ElementType;
  tag: string;
  size: 'large' | 'medium' | 'small';
  features?: string[];
  featuresColumn2?: string[];
  videoUrl?: string;
  backgroundIcon?: React.ElementType;
}

const BentoFeaturesGrid: React.FC = () => {
  const { theme } = useTheme();
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  
  // Theme-aware colors
  const bgColor = theme === 'dark' ? '#111111' : '#FCFCF9';
  const cardBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const cardBorderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const cardShadow = theme === 'dark' 
    ? '0 20px 40px -12px rgba(0,0,0,0.4), 0 8px 20px -8px rgba(0,0,0,0.3)'
    : '0 20px 40px -12px rgba(0,0,0,0.12), 0 8px 20px -8px rgba(0,0,0,0.06)';
  const glowBg = theme === 'dark'
    ? 'radial-gradient(ellipse at center, rgba(255,255,255,0.05) 0%, transparent 70%)'
    : 'radial-gradient(ellipse at center, rgba(0,0,0,0.1) 0%, transparent 70%)';
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const featureTextColor = theme === 'dark' ? '#CCCCCC' : '#333333';
  const iconColor = theme === 'dark' ? '#888888' : '#666666';
  const badgeBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const badgeBorderGradient = theme === 'dark' 
    ? 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%, transparent 100%)'
    : 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 50%, transparent 60%, transparent 100%)';
  const badgeBorderColor = theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
  const lineColor = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const gridLineColor = theme === 'dark' ? '#444444' : '#d0d0d0';

  const iconBoxStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
    backgroundSize: '5px 5px',
    WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
    maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
  };
  
  // Button colors
  const buttonBg = theme === 'dark' ? '#FFFFFF' : '#111111';
  const buttonText = theme === 'dark' ? '#111111' : '#FFFFFF';
  const buttonHoverBg = theme === 'dark' ? '#E5E5E5' : '#333333';

  const features: FeatureCard[] = [
    {
      title: 'Missing Inbound Leads',
      description: '78% of potential customers go with the first company to respond. Most businesses lose their leads simply by replying too late or not following up. With Storily, speed to lead is automatic.',
      icon: Users,
      tag: 'Leads',
      size: 'large',
      features: ['The Instant Text-Back', 'Automated Follow Up & Appointment Booking', 'The Unified Inbox', 'The 24/7 AI Concierge'],
      videoUrl: 'https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/V1%20-%20Ai%20Employee%20Male.mp4',
      backgroundIcon: PhoneIncoming
    },
    {
      title: 'Marketing Consistency',
      description: 'Stop struggling to stay relevant online. Plan a months content in 10 minutes & automate your social presence & content strategy so your brand stays active.',
      icon: Bot,
      tag: 'Content',
      size: 'medium',
      videoUrl: 'https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/V4%20Content%20Ai.mp4',
      backgroundIcon: CalendarDays
    },
    {
      title: 'Invisible Reputation',
      description: "It's like leaving your store shutters down in the digital world. Automate review requests, let AI respond & monitor brand authority.",
      icon: Zap,
      tag: 'Reputation',
      size: 'small',
      videoUrl: 'https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/V8%20Reputation%20Management%20Male%20(1).mp4',
      backgroundIcon: Star
    },
    {
      title: 'Manual Grunt Work',
      description: "If your business stops when you stop, you have a job, not a business. Stop the grind & automate everything here.",
      icon: Mail,
      tag: 'Automate',
      size: 'small',
      videoUrl: 'https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/V7%20Automation%20Builder.mp4',
      backgroundIcon: Wrench
    },
    {
      title: 'The "Owner-Dependent" Trap',
      description: "If you can't take a holiday without the business breaking, you've built a cage. Automate your systems & reclaim your freedom.",
      icon: Calendar,
      tag: 'Freedom',
      size: 'small',
      videoUrl: 'https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/explainer%20-o.mp4',
      backgroundIcon: Palmtree
    },
    {
      title: 'The Tab-Switching Fatigue',
      description: "Stop overpaying for ten different apps that don't talk to each other. Storily consolidates your disconnected tech into one platform.",
      icon: BarChart3,
      tag: 'Consolidate',
      size: 'large',
      features: ['Real-Time Blended ROI & CPA Tracking', 'One Fixed Monthly Bill', 'Massive Tech-Stack Cost Savings', 'All-In-One Reporting & Analytics'],
      featuresColumn2: ['Total Software Consolidation', 'Zero-Friction System Integration', 'End-to-End Data Consolidation', 'The Centralized Command Center'],
      videoUrl: 'https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/V23%20Comparison%20Video%20Male%20Version%20(1).mp4',
      backgroundIcon: Layers
    },
    {
      title: 'The Corporate Cost Creep',
      description: 'As your business scales, so do your... software costs? Storily kills the "success tax" by providing unlimited tools for one flat price.',
      icon: Palmtree,
      tag: 'Scale',
      size: 'medium',
      features: ['Unlimited User & Team Seats', 'Fixed-Price Scaling Model', 'End-to-End Operational Efficiency', 'Zero "Success Tax" Growth'],
      videoUrl: 'https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/V24%20Everything%20In%20One%20Place%20Male%20Version%20(1).mp4',
      backgroundIcon: Building2
    },
    {
      title: 'The Outreach Grind',
      description: "Cold outreach shouldn't feel like a full-time job. Storily automates your prospecting & lead generation so your calendar stays full effortlessly.",
      icon: Cog,
      tag: 'Outreach',
      size: 'medium',
      videoUrl: 'https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/explainer%20-o.mp4',
      backgroundIcon: Target
    },
  ];

  const CardComponent = ({ feature, onWatchDemo }: { feature: FeatureCard; onWatchDemo?: (videoUrl: string) => void }) => {
    const Icon = feature.icon;
    const BackgroundIcon = feature.backgroundIcon;
    
    return (
      <div className="relative h-full">
        {/* Glow/shadow effect behind the card */}
        <div 
          className="absolute -inset-3 rounded-2xl blur-xl opacity-40"
          style={{
            background: glowBg,
          }}
        />
        <div 
          className={`relative p-5 flex flex-col h-full transition-all duration-300 ease-out hover:shadow-lg hover:-translate-y-1 cursor-pointer overflow-hidden ${
            feature.size === 'large' ? 'min-h-[280px]' : 'min-h-[200px]'
          }`}
          style={{ 
            backgroundColor: cardBgColor,
            border: `1px solid ${cardBorderColor}`, 
            borderRadius: '12px',
            boxShadow: cardShadow,
          }}
        >
        {/* Background Icon with grid pattern - matching /compare pages */}
        {BackgroundIcon && (
          <div 
            className="absolute bottom-3 right-3 w-12 h-12 rounded-xl flex items-center justify-center pointer-events-none"
            style={iconBoxStyle}
          >
            <BackgroundIcon className="w-5 h-5" style={{ color: iconColor }} strokeWidth={1.5} />
          </div>
        )}
        
        {/* Tag Badge - uses stable global CSS classes */}
        <div className="relative z-10 mb-4">
          <AnimatedBorderPill text={feature.tag} icon={Icon} />
        </div>
        
        <div className="relative z-10 mb-3">
          <p 
            className="text-sm font-bold"
            style={{ color: headingColor }}
          >
            {feature.title}
          </p>
        </div>
        
        <p 
          className="relative z-10 text-sm leading-relaxed mb-4"
          style={{ color: textColor }}
        >
          {feature.description}
        </p>
        
        {feature.features && (
          <div className="relative z-10 mb-4">
            <div className={feature.featuresColumn2 ? "grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5" : ""}>
              <ul className="space-y-1.5">
                {feature.features.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-2.5 h-2.5 text-emerald-600" />
                    </div>
                    <span className="text-sm" style={{ color: featureTextColor }}>{item}</span>
                  </li>
                ))}
              </ul>
              {feature.featuresColumn2 && (
                <ul className="space-y-1.5">
                  {feature.featuresColumn2.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-2.5 h-2.5 text-emerald-600" />
                      </div>
                      <span className="text-sm" style={{ color: featureTextColor }}>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        
        {/* Watch Demo Button */}
        <div className="relative z-10 mt-auto">
          <button 
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            style={{ 
              backgroundColor: buttonBg,
              color: buttonText,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = buttonHoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = buttonBg;
            }}
            onClick={() => feature.videoUrl && onWatchDemo?.(feature.videoUrl)}
          >
            <Play className="w-3 h-3" fill="currentColor" />
            Watch Demo
          </button>
        </div>
        </div>
      </div>
    );
  };

  return (
    <section className="relative py-12 lg:py-16" style={{ backgroundColor: bgColor }}>
      {/* Vertical grid lines */}
      <div 
        className="absolute top-0 bottom-0 left-[5%] md:left-[calc(50%-616px)] w-px pointer-events-none z-0"
        style={{ backgroundColor: lineColor }}
      />
      <div 
        className="absolute top-0 bottom-0 right-[5%] md:right-[calc(50%-616px)] w-px pointer-events-none z-0"
        style={{ backgroundColor: lineColor }}
      />
      
      {/* Bottom horizontal lines (double) */}
      <div 
        className="absolute bottom-[38px] left-[5%] md:left-[calc(50%-616px)] right-[5%] md:right-[calc(50%-616px)] h-px pointer-events-none z-0"
        style={{ 
          background: `linear-gradient(to right, transparent, ${lineColor} 5%, ${lineColor} 95%, transparent)`
        }}
      />
      <div 
        className="absolute bottom-0 left-[5%] md:left-[calc(50%-616px)] right-[5%] md:right-[calc(50%-616px)] h-px pointer-events-none z-0"
        style={{ 
          background: `linear-gradient(to right, transparent, ${lineColor} 5%, ${lineColor} 95%, transparent)`
        }}
      />

      <div className="relative z-10 max-w-[960px] mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          {/* Badge - uses stable global CSS classes */}
          <div className="flex justify-center mb-4">
            <AnimatedBorderPill text="Features" icon={Sparkles} />
          </div>
          
          <h2 
            className="text-3xl lg:text-4xl font-bold tracking-tight"
            style={{ color: headingColor }}
          >
            ...& the problems they solve.
          </h2>
        </div>

        
        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Row 1: The Corporate Creep (left, 2 cols), The Outreach Grind (right, 1 col) */}
          <div className="lg:col-span-2">
            <CardComponent feature={features[6]} onWatchDemo={setActiveVideo} />
          </div>
          <div>
            <CardComponent feature={features[7]} onWatchDemo={setActiveVideo} />
          </div>
          
          {/* Row 2: Invisible Reputation (left), original Manual Grunt Work, original Owner-Dependent */}
          <div>
            <CardComponent feature={features[2]} onWatchDemo={setActiveVideo} />
          </div>
          <div>
            <CardComponent feature={features[3]} onWatchDemo={setActiveVideo} />
          </div>
          <div>
            <CardComponent feature={features[4]} onWatchDemo={setActiveVideo} />
          </div>
          
          {/* Row 3: Tab-Switching Fatigue (full width) */}
          <div className="lg:col-span-3">
            <CardComponent feature={features[5]} onWatchDemo={setActiveVideo} />
          </div>
          
          {/* Row 3: Marketing Consistency (left), Missing Inbound Leads (right, 2 cols) */}
          <div>
            <CardComponent feature={features[1]} onWatchDemo={setActiveVideo} />
          </div>
          <div className="lg:col-span-2">
            <CardComponent feature={features[0]} onWatchDemo={setActiveVideo} />
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setActiveVideo(null)}
        >
          <div 
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActiveVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <video
              autoPlay
              controls
              className="w-full aspect-video rounded-lg"
              src={activeVideo}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default BentoFeaturesGrid;
